import { captureException } from '@/utils/sentry';
import {
  HOST_ROUTE_MAP,
  navigateToSalesPortalRoute,
  ROUTE_REQUIRED_PARAMS,
} from '@/experiences/sales-portal/sales-portal-route-config';
import {
  MicroProductWebViewRef,
  NavigateToHostPayload,
} from '@domain/microproducts/components/micro-product-webview-types';
import { NavigationProp, ParamListBase } from '@react-navigation/native';
import { RefObject, useCallback } from 'react';
import { SalesPortalStackParams } from '@/navigators/types';
import { SalsaMessageType } from '@/generated/salsa-protocol';

/**
 * Builds the `onNavigateToHost` handler shared by every sales-portal
 * microproduct screen (the generic `Base` loader and domain-scoped screens
 * like pricing). Resolves the salsa route alias, validates required params,
 * and dispatches a nested-navigator action so it works even when the target
 * tab's stack hasn't mounted yet. Posts a NAVIGATE_ERROR back to the SPA on
 * unknown route / missing params.
 */
export const useMicroProductHostNavigation = (
  navigation: { dispatch: NavigationProp<ParamListBase>['dispatch'] },
  webViewRef: RefObject<MicroProductWebViewRef | null>,
) =>
  useCallback(
    (payload: NavigateToHostPayload) => {
      const internalRoute = HOST_ROUTE_MAP[payload.route];
      if (!internalRoute) {
        captureException(
          new Error(`[salsa:navigate-host] Unknown route "${payload.route}"`),
          {
            extra: {
              knownRoutes: Object.keys(HOST_ROUTE_MAP),
              requestedRoute: payload.route,
            },
          },
        );
        webViewRef.current?.sendMessage({
          reason: `Unknown host route "${payload.route}". Supported: ${Object.keys(HOST_ROUTE_MAP).join(', ')}`,
          route: payload.route,
          type: SalsaMessageType.NAVIGATE_ERROR,
        });
        return;
      }
      const requiredParams = ROUTE_REQUIRED_PARAMS[internalRoute];
      if (requiredParams) {
        const missing = requiredParams.filter((p) => !payload.params?.[p]);
        if (missing.length > 0) {
          captureException(
            new Error(
              `[salsa:navigate-host] Missing params for "${payload.route}": ${missing.join(', ')}`,
            ),
            {
              extra: {
                missing,
                provided: payload.params,
                route: payload.route,
              },
            },
          );
          webViewRef.current?.sendMessage({
            reason: `Missing required params: ${missing.join(', ')}`,
            route: payload.route,
            type: SalsaMessageType.NAVIGATE_ERROR,
          });
          return;
        }
      }
      navigateToSalesPortalRoute(
        navigation,
        internalRoute,
        payload.params as SalesPortalStackParams[typeof internalRoute],
      );
    },
    [navigation, webViewRef],
  );
